import { Injectable } from "@angular/core";
import { RcContext } from "../context/rcContext";
import { RemoteControlService } from "app/modules/remote-control/services/remote-control.service";
import { DeviceContext } from "../context/deviceContext";
import { ScaleSize } from "../definitions";

@Injectable({
    providedIn: 'root'
})
export class PanService {

    public panEnabled: boolean;

    private canvas: any;
    private mouseWheelEvt: string;
    private dragging: boolean;
    private clickX: number;
    private clickY: number;
    private zoom: (factor: number, force: boolean) => void;

    constructor(private remoteControlService: RemoteControlService) {
        this.panEnabled = false;
        this.dragging = false;
        this.clickX = -1;
        this.clickY = -1;
        this.mouseWheelEvt = /Firefox/i.test(navigator.userAgent) ? 'DOMMouseScroll' : 'mousewheel'
    }

    public isPanEnabled(): boolean {
        return this?.panEnabled || false;
    }

    // Turn pan mode on or off
    public pan(panOn: boolean, zoom: (factor: number, force: boolean) => void) {
        const deviceInput = RcContext.instance.deviceInput;
        if (deviceInput !== undefined && deviceInput !== null) {
            // this service should have a public method handleEvent(event: any): boolean
            this.canvas = deviceInput.setZoomController(this);
            this.controlPanMode(panOn);
            this.zoom = zoom;
        }
    }

    // Turn pan mode on/off updating UI
    public controlPanMode = (on: boolean) => {
        if (on) {
            this.setCanvasCursor('move');
            this.canvas.addEventListener(this.mouseWheelEvt, this.onMouseScroll, false);
            document.addEventListener('mouseleave', this.handleEvent, false);
            this.canvas.addEventListener('mouseleave', this.handleEvent, false);
            document.addEventListener('keyup', this.handleEvent, false);
        } else {
            this.dragging = false;
            if (this.canvas) {
                this.setCanvasCursor('');
                document.removeEventListener('mouseleave', this.handleEvent, false);
                this.canvas.removeEventListener(this.mouseWheelEvt, this.onMouseScroll, false);
                this.canvas.removeEventListener('mouseleave', this.handleEvent, false);
            }
            document.removeEventListener('keyup', this.handleEvent, false);
        }
        this.panEnabled = on;
    }

    private setCanvasCursor(cursor: string): void {
        if (this.canvas.style) {
            this.canvas.style.cursor = cursor;
        }
        if (RcContext.instance.deviceContext?.videoStreaming) {
            const context = this.remoteControlService.getRemoteControlContext();
            context?.deviceViewHandler?.setCanvasCursor(cursor);
        }
    }

    private onMouseScroll = (event: any) => {
        if (this.panEnabled) {
            this.setCanvasCursor('move');
            if (
                (this.mouseWheelEvt === 'mousewheel' && event.wheelDelta < 0) ||
                (this.mouseWheelEvt === 'DOMMouseScroll' && event.detail > 0)
            ) {
                this.zoom(ScaleSize.SCALE_STEP_DOWN, false);
            } else {
                this.zoom(ScaleSize.SCALE_STEP_UP, false);
            }
        }
      }

    // Handle mouse and keyboard events on behalf of DeviceInput
    public handleEvent = (event: any): boolean => {
        if (this.panEnabled && this.canvas !== null) {
            switch (event.type) {
                case 'mousedown':
                    this.dragging = true;
                    this.clickX = event.clientX;
                    this.clickY = event.clientY;
                    break;

                case 'mousemove':
                    this.setCanvasCursor('move');
                    if (this.dragging) {
                        this.adjustCanvasShifts(event.clientX - this.clickX, event.clientY - this.clickY);
                        this.clickX = event.clientX;
                        this.clickY = event.clientY;
                    }
                    break;

                case 'mouseup':
                case 'mouseleave':
                case 'mouseout':
                    if (this.dragging) {
                        this.dragging = false;
                        this.clickX = -1;
                        this.clickY = -1;
                    }
                    break;

                case 'keyup':
                    if (event.keyCode === 27) {
                        this.controlPanMode(false);
                    }
                    break;

                case 'keypress':
                case 'keydown':
                    // block them in pan mode for now
                    break;
              }
            return true;
        }
        return false;
    }

    // Adjust placement based on shifts
  private adjustCanvasShifts(x: number, y: number) {
    if (RcContext.instance.deviceContext?.videoStreaming) {
        const context = this.remoteControlService.getRemoteControlContext();
        if (context && context.deviceViewHandler && (x !== 0 || y !== 0)) {
            context.deviceViewHandler.offsetView(x, y);
        }
        return;
    }

    const ctx = RcContext.instance.deviceContext as DeviceContext;
    if (ctx.canvas && ctx.canvas.clientWidth !== 0 && ctx.canvas.clientHeight !== 0) {
        RcContext.instance.deviceScreenTransformService?.offsetView(x, y);
    }
  }
}
